<template>
  <div class="py-4 container-fluid">
    <div class="row">
      <div class="col-12">
        <div class="card">
          <div class="card-header">
            <h5 class="mb-0">{{ $t('manageImages') }}</h5>
            <p class="mb-0 text-sm">{{ $t('exercise') }}: {{ exerciseName || $t('loading') }}</p>
          </div>
          <div class="card-body">
            <div v-if="images.length === 0" class="col-12 text-center">
              <p class="text-muted">{{ $t('noImagesFound') }}</p>
            </div>
            <div class="d-flex flex-wrap">
              <div
                v-for="image in images"
                :key="image.id"
                class="card shadow-sm"
                style="width: 18rem"
              >
                <div class="card-body text-center">
                  <img
                    :src="image.full_url"
                    :alt="$t('exerciseImage')"
                    class="img-fluid rounded"
                    style="max-height: 150px; object-fit: cover"
                  />
                  <div class="mt-3">
                    <input
                      type="file"
                      :ref="'fileInput-' + image.id"
                      @change="handleImageUpload($event, image.id)"
                      class="d-none"
                      accept="image/*"
                    />
                    <button class="btn btn-warning btn-sm me-2" @click="prepareUpdateImage(image)">
                      <i class="fas fa-pen"></i> {{ $t('update') }}
                    </button>
                    <button class="btn btn-danger btn-sm" @click="confirmDeleteImage(image)">
                      <i class="fas fa-trash"></i> {{ $t('delete') }}
                    </button>
                  </div>
                </div>
              </div>
              <div
                v-if="images.length < maxImages"
                class="card shadow-sm upload-area"
                style="width: 18rem"
                @dragover.prevent="dragOver = true"
                @dragleave="dragOver = false"
                @drop.prevent="handleDrop"
                :class="{ 'border-primary': dragOver }"
              >
                <div class="card-body text-center">
                  <div v-if="dragOver" class="drop-zone-active">
                    <i class="fas fa-cloud-upload-alt fa-3x mb-2"></i>
                    <p>{{ $t('dropImageHere') }}</p>
                  </div>
                  <div v-else>
                    <h6 class="mb-3">{{ $t('addImage') }}</h6>
                    <i class="fas fa-image fa-3x mb-2 text-muted"></i>
                    <p>{{ $t('dragDropOrClick') }}</p>
                    <input
                      type="file"
                      ref="fileInput"
                      @change="handleImageUpload"
                      class="d-none"
                      accept="image/*"
                    />
                    <button class="btn btn-primary btn-sm" @click="$refs.fileInput.click()">
                      <i class="fas fa-upload"></i> {{ $t('selectImage') }}
                    </button>
                    <div v-if="selectedImage" class="mt-2">
                      <img
                        :src="previewImage"
                        :alt="$t('preview')"
                        class="img-thumbnail"
                        style="max-height: 100px"
                      />
                      <p class="small text-muted mt-1 mb-0">
                        {{ selectedImage.name }} ({{ formatFileSize(selectedImage.size) }})
                      </p>
                    </div>
                    <button
                      v-if="selectedImage"
                      class="btn btn-success btn-sm mt-2"
                      @click="uploadOrUpdateImage"
                    >
                      <i class="fas fa-check"></i> {{ $t('send') }}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
    <div class="d-flex justify-content-center mt-4" v-if="totalImages > perPage">
      <nav aria-label="Page navigation">
        <ul class="pagination">
          <li class="page-item" :class="{ disabled: currentPage === 1 }">
            <button class="page-link" @click="previousPage">{{ $t('previous') }}</button>
          </li>
          <li
            class="page-item"
            v-for="page in totalPages"
            :key="page"
            :class="{ active: currentPage === page }"
          >
            <button class="page-link" @click="goToPage(page)">{{ page }}</button>
          </li>
          <li class="page-item" :class="{ disabled: currentPage === totalPages }">
            <button class="page-link" @click="nextPage">{{ $t('next') }}</button>
          </li>
        </ul>
      </nav>
    </div>
  </div>
</template>

<script>
import exercicioRepository from '@/services/api/exercicioRepository'
import Swal from 'sweetalert2'

export default {
  props: {
    exerciseId: {
      type: [Number, String], // Accept both Number and String to handle route params
      required: true,
    },
    maxImages: {
      type: Number,
      default: 3,
    },
  },
  data() {
    return {
      exerciseName: null,
      images: [],
      selectedImage: null,
      previewImage: null,
      isUpdating: false,
      updatingImageId: null,
      dragOver: false,
      currentPage: 1,
      perPage: 6,
      totalImages: 0,
    }
  },
  computed: {
    totalPages() {
      return Math.ceil(this.totalImages / this.perPage)
    },
  },
  methods: {
    formatFileSize(size) {
      if (size < 1024) return `${size} bytes`
      else if (size < 1024 * 1024) return `${(size / 1024).toFixed(1)} KB`
      else return `${(size / (1024 * 1024)).toFixed(2)} MB`
    },
    async fetchExerciseDetails() {
      try {
        const response = await exercicioRepository.ExercicioGetById(this.exerciseId)
        this.exerciseName = response.data.data.name || 'Exercício desconhecido' // Access nested name
      } catch (error) {
        console.error('Erro ao buscar detalhes do exercício:', error)
        this.exerciseName = 'Erro ao carregar nome do exercício'
      }
    },
    async fetchImages() {
      try {
        const response = await exercicioRepository.ExercicioGetImages(this.exerciseId)
        if (response.data && Array.isArray(response.data.data)) {
          this.images = response.data.data
          this.totalImages = response.data.total || this.images.length
        } else {
          this.images = []
          this.totalImages = 0
        }
      } catch (error) {
        console.error('Erro ao buscar imagens:', error)
        this.showError('Erro ao carregar imagens', error)
      }
    },
    previousPage() {
      if (this.currentPage > 1) {
        this.currentPage--
        this.fetchImages()
      }
    },
    nextPage() {
      if (this.currentPage < this.totalPages) {
        this.currentPage++
        this.fetchImages()
      }
    },
    goToPage(page) {
      this.currentPage = page
      this.fetchImages()
    },
    handleImageUpload(event, imageId) {
      const file = event.target.files[0]

      if (!file) {
        this.selectedImage = null
        this.previewImage = null
        return
      }

      // Validate file type
      const validTypes = ['image/jpeg', 'image/png', 'image/jpg']
      if (!validTypes.includes(file.type)) {
        Swal.fire({
          title: 'Formato inválido',
          text: 'Por favor, envie apenas imagens JPEG, PNG, GIF ou WebP.',
          icon: 'error',
          customClass: {
            confirmButton: 'btn bg-gradient-danger',
          },
          buttonsStyling: false,
        })
        event.target.value = '' // Reset file input
        return
      }

      // Validate file size (5MB max)
      const maxSize = 5 * 1024 * 1024 // 5MB
      if (file.size > maxSize) {
        Swal.fire({
          title: 'Arquivo muito grande',
          text: 'A imagem não pode exceder 5MB.',
          icon: 'error',
          customClass: {
            confirmButton: 'btn bg-gradient-danger',
          },
          buttonsStyling: false,
        })
        event.target.value = '' // Reset file input
        return
      }

      // Create preview
      const reader = new FileReader()
      reader.onload = (e) => {
        this.previewImage = e.target.result
      }
      reader.readAsDataURL(file)

      this.selectedImage = file
      this.uploadOrUpdateImage(imageId) // Automatically upload after selecting the file

      // Reset file input to allow re-uploading the same file
      event.target.value = ''
    },
    async uploadOrUpdateImage(imageId) {
      if (!this.selectedImage) {
        Swal.fire({
          title: 'Erro!',
          text: 'Nenhuma imagem foi selecionada.',
          icon: 'error',
          customClass: {
            confirmButton: 'btn bg-gradient-danger',
          },
          buttonsStyling: false,
        })
        return
      }

      const formData = new FormData()
      formData.append('image', this.selectedImage)
      formData.append('exercise_id', this.exerciseId)

      const loadingSwal = Swal.fire({
        title: 'Processando...',
        html: 'Por favor, aguarde enquanto a imagem está sendo enviada.',
        allowOutsideClick: false,
        didOpen: () => {
          Swal.showLoading()
        },
        customClass: {
          confirmButton: 'btn bg-gradient-primary',
        },
        buttonsStyling: false,
      })

      try {
        let response
        if (imageId) {
          // Update existing image
          response = await exercicioRepository.ExercicioUpdateImage(imageId, formData)
        } else {
          // Add new image
          response = await exercicioRepository.ExercicioAddImage(formData, this.exerciseId)
        }

        await loadingSwal.close()

        if (response.data && response.data.data) {
          Swal.fire({
            title: 'Sucesso!',
            text: imageId ? 'Imagem atualizada com sucesso!' : 'Imagem adicionada com sucesso!',
            icon: 'success',
            customClass: {
              confirmButton: 'btn bg-gradient-success',
            },
            buttonsStyling: false,
          })

          this.fetchImages() // Refresh the image list
          this.resetUploadState()
        } else {
          throw new Error(response.data.message || 'Erro ao processar a imagem.')
        }
      } catch (error) {
        await loadingSwal.close()
        this.showError('Erro ao enviar imagem', error)
      }
    },
    prepareUpdateImage(image) {
      this.isUpdating = true
      this.updatingImageId = image.id
      this.selectedImage = null
      this.previewImage = null
      this.$refs[`fileInput-${image.id}`].click() // Open the file input for the specific card
    },
    resetUploadState() {
      this.selectedImage = null
      this.previewImage = null
      this.isUpdating = false
      this.updatingImageId = null
    },
    confirmDeleteImage(image) {
      Swal.fire({
        title: 'Tem certeza?',
        text: `Deseja excluir a imagem criada em ${new Date(
          image.created_at,
        ).toLocaleDateString()}?`,
        icon: 'warning',
        showCancelButton: true,
        confirmButtonText: 'Sim, excluir!',
        cancelButtonText: 'Não, cancelar!',
        customClass: {
          confirmButton: 'btn bg-gradient-success',
          cancelButton: 'btn bg-gradient-danger',
        },
        buttonsStyling: false,
      }).then((result) => {
        if (result.isConfirmed) {
          this.deleteImage(image.id)
        }
      })
    },
    async deleteImage(imageId) {
      const loadingSwal = Swal.fire({
        title: 'Excluindo...',
        html: 'Por favor, aguarde enquanto a imagem está sendo removida.',
        allowOutsideClick: false,
        didOpen: () => {
          Swal.showLoading()
        },
        customClass: {
          confirmButton: 'btn bg-gradient-primary',
        },
        buttonsStyling: false,
      })

      try {
        const response = await exercicioRepository.ExercicioDeleteImage(imageId)
        await loadingSwal.close()

        if (
          response.data &&
          (response.data.success || response.data.message === 'Imagem excluída com sucesso.')
        ) {
          Swal.fire({
            title: 'Removido!',
            text: response.data.message || 'A imagem foi removida com sucesso!',
            icon: 'success',
            customClass: {
              confirmButton: 'btn bg-gradient-success',
            },
            buttonsStyling: false,
          })
          this.fetchImages()
        } else {
          // Handle unexpected response structure
          throw new Error(response.data.message || 'Erro desconhecido ao excluir a imagem.')
        }
      } catch (error) {
        await loadingSwal.close()
        console.error('Erro ao excluir imagem:', error)
        this.showError('Erro ao excluir imagem', error)
      }
    },
  },
  watch: {
    exerciseId: {
      immediate: true,
      handler(newVal) {
        this.fetchExerciseDetails()
        this.fetchImages()
      },
    },
  },
}
</script>

<style scoped>
.d-flex {
  display: flex;
  justify-content: center; /* Center the images horizontally */
  flex-wrap: wrap;
}

.card {
  margin: 1rem; /* Add spacing between the image cards */
}

.card-body img {
  max-height: 150px;
  object-fit: cover;
}
.card-body {
  padding: 15px;
}
.upload-area {
  transition: all 0.3s ease;
  border: 1px solid #e3e6f0;
  cursor: pointer;
  min-height: 200px;
  display: flex;
  align-items: center;
  justify-content: center;
}
.upload-area:hover {
  border-color: #4e73df;
}
.drop-zone-active {
  padding: 20px;
  background-color: rgba(78, 115, 223, 0.1);
  border-radius: 5px;
  width: 100%;
}
.pagination .page-item .page-link {
  color: #4e73df;
}
.pagination .page-item.active .page-link {
  background-color: #4e73df;
  border-color: #4e73df;
  color: white;
}
</style>
